import { createContext, useContext, useEffect, type ReactNode } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ApiError } from '@clouway/api'

import { NetworkError } from '@/api'
import { fetchUserInfo, postLogout } from '../api/api'
import { userInfoKey } from '../keyFactory'
import type { SessionUser } from '../types'

export type SessionStatus = 'loading' | 'authenticated' | 'unauthenticated' | 'error'

export interface SessionContextValue {
  status: SessionStatus
  user: SessionUser | null
  logout: () => Promise<void>
}

const SessionContext = createContext<SessionContextValue>({
  status: 'loading',
  user: null,
  logout: async () => {},
})

function assign(url: string) {
  window.location.assign(url)
}

// An unauthenticated probe surfaces as a 401 or — when browserauth 307s to the
// IDP and CORS kills the followed redirect — a NetworkError. A same-origin BFF
// that is actually down could not have served the SPA itself, so both mean: no
// session.
function isUnauthenticated(error: unknown): boolean {
  return error instanceof NetworkError || (error instanceof ApiError && error.status === 401)
}

export interface SessionProviderProps {
  children: ReactNode
  /** Full-page navigation, injectable for specs. */
  navigate?: (url: string) => void
}

export function SessionProvider({ children, navigate = assign }: SessionProviderProps) {
  const userInfo = useQuery({
    queryKey: userInfoKey(),
    queryFn: fetchUserInfo,
  })

  const logoutMutation = useMutation({
    mutationFn: postLogout,
    onSuccess: () => navigate('/'),
  })

  const unauthenticated = userInfo.isError && isUnauthenticated(userInfo.error)

  useEffect(() => {
    if (unauthenticated) {
      navigate('/auth/login')
    }
  }, [unauthenticated, navigate])

  if (userInfo.isLoading || unauthenticated) {
    return null
  }
  if (userInfo.isError) {
    return (
      <div className="text-muted-foreground flex h-screen items-center justify-center">
        Session unavailable. Refresh to retry.
      </div>
    )
  }

  const value: SessionContextValue = {
    status: 'authenticated',
    user: userInfo.data,
    logout: async () => {
      await logoutMutation.mutateAsync()
    },
  }

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession() {
  return useContext(SessionContext)
}
